import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { IconButton, Tooltip, Typography, Button, withStyles } from '@material-ui/core';
import { createHistory, hasPermission } from '../../sc-utilities';
import { FindInPage, SettingsRemote } from '@material-ui/icons';
import { copyToClipBoard } from '../../sc-utilities';
import { snackActions } from '../../sc-utilities/SnackBar';
import ProductOverviewModal from './ProductOverviewModal';
import ProductsMinimal from './ProductsMinimal';
import ProductsAll, { getGroupsLookup } from './ProductsAll';
import * as action from '../../np-dashboard/CustomerBar/store/actions';
import * as actions from './store/productOverviewActions';
import { CancelToken } from 'axios';
import { CustomChip, indexStyle, getEraseStatus, getVerificationStatus } from './utils';
import * as groupActions from "../../sc-group/Group/store/groupActions";

const styles = theme => (indexStyle(theme));

const ProductOverview = props => {
    const { classes, translations, permissions, onFetchErasureMethods, activeCustomer, activeCustomerName, onChangeActiveCustomer, fetchGroups } = props;

    const tableRef = useRef();

    const [redirectOptions, setRedirectOptions] = useState({ redirect: false, productID: null, redirectPath: '', open: false, product: '', customerID: null });
    const [erasureMethods, setErasureMethod] = useState({ data: null, loading: false });
    const [currentView, updateView] = useState(null);

    const [groups, setGroups] = useState([]);
    const [groupsLookup, setGroupsLookup] = useState({});
    const [isGroupsLoading, setIsGroupsLoading] = useState(true);

    useEffect(() => {
        let erasuresCancel = CancelToken.source();
        const fetchErasureMethods = async () => {
            let erasureMethodData = await onFetchErasureMethods({ ActiveCustomerID: activeCustomer, Visible: true }, { erasuresCancel: erasuresCancel });
            let formattedErasureMethods = erasureMethodData.reduce(
                (obj, item) => Object.assign(obj, { [item.value]: translations[item.value] || item.displayValue }), {});
            setErasureMethod({ data: formattedErasureMethods, loading: true });
        };

        if (!tableRef?.current) {
            fetchErasureMethods();
        }

        const getGroups = async () => {
            const resultGroups = await fetchGroups({ ActiveCustomerID: activeCustomer });
            if (resultGroups) {
                setGroups(resultGroups.data);
                setGroupsLookup(getGroupsLookup(resultGroups.data, translations, permissions));
                setIsGroupsLoading(false);

            }
        }

        if (hasPermission(permissions, 'group_list')) {
            getGroups();
        }

        return () => {
            erasuresCancel.cancel('Canceled by user');
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const goToColli = (event, customerID, name) => {
        let colliInventoryID = event.currentTarget.value;
        if (customerID !== activeCustomer) {
            setRedirectOptions({
                ...redirectOptions,
                open: true,
                product: name,
                customerID: customerID,
                redirectPath: `/logistic/container?ColliInventoryID=${colliInventoryID}`
            });
            return;
        }
        createHistory();
        setRedirectOptions({
            ...redirectOptions,
            redirect: true,
            redirectPath: `/logistic/container?ColliInventoryID=${colliInventoryID}`
        });
    };

    const createPaletteActionForProduct = (statistic, paletteType, classes, customerID, name) => {
        return statistic ?
            <Button color='primary'
                key={statistic} variant="contained" className={classes.paletteButtons} value={statistic}
                size='small' onClick={event => {
                    switch (paletteType) {
                        case 'MobileProduct':
                            return hasPermission(permissions, 'product_list') ? goToMobileProduct(event, customerID, name) : null
                        case 'PCProduct':
                            return hasPermission(permissions, 'product_list') ? goToPcProduct(event, customerID, name) : null
                        case 'DisplayProduct':
                            return hasPermission(permissions, 'product_list') ? goToDisplayProduct(event, customerID, name) : null
                        case 'StorageProduct':
                            return hasPermission(permissions, 'product_list') ? goToStorageProduct(event, customerID, name) : null;
                        case 'WatchProduct':
                            return hasPermission(permissions, 'product_list') ? goToWatchProduct(event, customerID, name) : null;
                        case 'HeadphoneProduct':
                            return hasPermission(permissions, 'product_list') ? goToHeadphoneProduct(event, customerID, name) : null;
                        default:
                            return null;
                    }
                }}>
                {statistic}
            </Button> : '[empty]';
    }

    const goToOrder = (event, customerID, name) => {
        let orderInventoryID = event.currentTarget.value;
        if (customerID !== activeCustomer) {
            setRedirectOptions({
                ...redirectOptions,
                open: true,
                product: name,
                customerID: customerID,
                redirectPath: `/logistic/orders?OrderInventoryID=${orderInventoryID}`
            });
            return;
        }
        createHistory();
        setRedirectOptions({
            ...redirectOptions,
            redirect: true,
            redirectPath: `/logistic/orders?OrderInventoryID=${orderInventoryID}`
        });
    }

    const goToTransportColli = (event, customerID, name) => {
        let transportColliInventoryID = event.currentTarget.value;
        if (customerID !== activeCustomer) {
            setRedirectOptions({
                ...redirectOptions,
                open: true,
                product: name,
                customerID: customerID,
                redirectPath: `/logistic/transport-container?TransportColliInventoryID=${transportColliInventoryID}`
            });
            return;
        }
        createHistory();
        setRedirectOptions({
            ...redirectOptions,
            redirect: true,
            redirectPath: `/logistic/transport-container?TransportColliInventoryID=${transportColliInventoryID}`
        });
    }

    const handleOpenAnydesk = async (event, rowData) => {
        copyToClipBoard(rowData.RemotePassword);
        snackActions.snackBarComponent((<Typography variant={'caption'} ><a className={classes.remote} rel="noopener noreferrer" href="https://anydesk.com/" target="_blank">
            {translations['securaze_remote_anydesk_snackbar_message'] || "Password is copied to clipboard. NOTE: Make sure you have installed AnyDesk"}
        </a></Typography>), 'info');
        const element = document.createElement('a');
        element.href = `anydesk:${rowData.RemoteID}`;
        element['data-href-alt'] = "https://anydesk.com/"
        return element.click();
    };

    const handleOpenRustdesk = async (event, rowData) => {
        copyToClipBoard(rowData.RemotePassword);
        snackActions.snackBarComponent((
            <Typography variant={'caption'}>
                <a
                    className={classes.remote}
                    rel="noopener noreferrer"
                    href="https://rustdesk.com/"
                    target="_blank"
                >
                    {translations['securaze_remote_rustdesk_snackbar_message'] || "Password is copied to clipboard. NOTE: Make sure you have installed RustDesk"}
                </a>
            </Typography>
        ), 'info');
        const element = document.createElement('a');
        element.href = `rustdesk://${rowData.RemoteID}`;
        element['data-href-alt'] = "https://rustdesk.com/";
        return element.click();
    };

    const goToMobileProductPage = (event, customerID, name) => {
        const activeCustomerParams = `&ActiveCustomerID=${customerID}&ActiveCustomerName=${name}`;
        let mobileProductInventoryID = event.currentTarget.value;
        if (customerID !== activeCustomer) {
            setRedirectOptions({
                ...redirectOptions,
                open: true,
                product: name,
                customerID: customerID,
                redirectPath: `/mobile-product/details?productID=${mobileProductInventoryID}&type=MobileProduct${activeCustomerParams}`
            });
            return;
        }
        createHistory();
        setRedirectOptions({
            ...redirectOptions,
            redirect: true,
            mobileProductInventoryID: event.currentTarget.value,
            redirectPath: `/mobile-product/details?productID=${mobileProductInventoryID}&type=MobileProduct${activeCustomerParams}`
        });
    };

    const goToStorageProductPage = (event, customerID, name) => {
        const activeCustomerParams = `&ActiveCustomerID=${customerID}&ActiveCustomerName=${name}`;
        let storageProductInventoryID = event.currentTarget.value;
        if (customerID !== activeCustomer) {
            setRedirectOptions({
                ...redirectOptions,
                open: true,
                product: name,
                customerID: customerID,
                redirectPath: `/storage-product/details?productID=${storageProductInventoryID}&type=StorageProduct${activeCustomerParams}`
            });
            return;
        }
        createHistory();
        setRedirectOptions({
            ...redirectOptions,
            redirect: true,
            redirectPath: `/storage-product/details?productID=${storageProductInventoryID}&type=StorageProduct${activeCustomerParams}`
        });
    };

    const goToPcProductPage = (event, customerID, name) => {
        const activeCustomerParams = `&ActiveCustomerID=${customerID}&ActiveCustomerName=${name}`;
        let pcProductInventoryID = event.currentTarget.value;
        if (customerID !== activeCustomer) {
            setRedirectOptions({
                ...redirectOptions,
                open: true,
                product: name,
                customerID: customerID,
                redirectPath: `/pc-product/details?productID=${pcProductInventoryID}&type=PCProduct${activeCustomerParams}`
            });
            return;
        }
        createHistory();
        setRedirectOptions({
            ...redirectOptions,
            redirect: true,
            redirectPath: `/pc-product/details?productID=${pcProductInventoryID}&type=PCProduct${activeCustomerParams}`
        });
    };

    const goToWatchProductPage = (event, customerID, name) => {
        const activeCustomerParams = `&ActiveCustomerID=${customerID}&ActiveCustomerName=${name}`;
        let watchProductInventoryID = event.currentTarget.value;
        if (customerID !== activeCustomer) {
            setRedirectOptions({
                ...redirectOptions,
                open: true,
                product: name,
                customerID: customerID,
                redirectPath: `/watch-product/details?productID=${watchProductInventoryID}&type=WatchProduct${activeCustomerParams}`
            });
            return;
        }
        createHistory();
        setRedirectOptions({
            ...redirectOptions,
            redirect: true,
            watchProductInventoryID: event.currentTarget.value,
            redirectPath: `/watch-product/details?productID=${watchProductInventoryID}&type=WatchProduct${activeCustomerParams}`
        });
    }

    const goToHeadphoneProductPage = (event, customerID, name) => {
        const activeCustomerParams = `&ActiveCustomerID=${customerID}&ActiveCustomerName=${name}`;
        let headphoneProductInventoryID = event.currentTarget.value;
        if (customerID !== activeCustomer) {
            setRedirectOptions({
                ...redirectOptions,
                open: true,
                product: name,
                customerID: customerID,
                redirectPath: `/headphone-product/details?productID=${headphoneProductInventoryID}&type=HeadphoneProduct${activeCustomerParams}`
            });
            return;
        }
        createHistory();
        setRedirectOptions({
            ...redirectOptions,
            redirect: true,
            headphoneProductInventoryID: event.currentTarget.value,
            redirectPath: `/headphone-product/details?productID=${headphoneProductInventoryID}&type=HeadphoneProduct${activeCustomerParams}`
        });
    }
    
    const redirectToCustomer = id => {
        createHistory();
        setRedirectOptions({
            ...redirectOptions,
            redirect: true,
            redirectPath: `/admin/customers/details?customerID=${id}`
        });
    };

    const createPaletteAction = (statistic, paletteType, customerID, name) => {
        return statistic[`${paletteType}ID`] ?
            <Button className={classes['paletteButtons']} color='primary'
                key={statistic[`${paletteType}ID`]} variant="contained"
                value={statistic && statistic[paletteType][`${paletteType}InventoryID`]} size='small'
                onClick={event => {
                    if (paletteType) {
                        switch (paletteType) {
                            case 'Colli':
                                return hasPermission(permissions, 'colli_list') ? goToColli(event, customerID, name) : null
                            case 'Order':
                                return hasPermission(permissions, 'order_list') ? goToOrder(event, customerID, name) : null
                            case 'TransportColli':
                                return hasPermission(permissions, 'transport_colli_list') ? goToTransportColli(event, customerID, name) : null

                            default:
                                return null;
                        }
                    }
                }}
            >
                {statistic[paletteType][`${paletteType}InventoryID`]}
            </Button> : '[empty]';
    };

    const getStorageComponentData = (components, field) => {
        if (components) {
            let prettyPrint = components.slice(0, 3).map((component, index) => {
                let storageComponent = typeof component.StorageComponents === 'object' && component.StorageComponents instanceof Array && component.StorageComponents.length > 0 ? component.StorageComponents[0] : false;
                if (!storageComponent) {
                    return 'n/a';
                }
                switch (field) {
                    case 'WipeStatus':
                        if (storageComponent[field] === 'inprogress') {
                            return <CustomChip key={`${component.ComponentID}_wipeStatus`} value={`${storageComponent.WipeProgress ? storageComponent.WipeProgress : 0}% ${translations && translations['in_progress'] ? translations['in_progress'] : 'In progress'}`}
                                className={`chip_${getEraseStatus(storageComponent[field], translations, true)}`} />;
                        }
                        let labelText1 = getEraseStatus(storageComponent[field], translations);
                        return <CustomChip key={`${component.ComponentID}_wipeStatus`} value={labelText1}
                            className={`chip_${getEraseStatus(storageComponent[field], translations, true)}`} />;
                    case 'VerificationStatus':
                        if (storageComponent[field] === 'inprogress') {
                            return <CustomChip key={`${component.ComponentID}_verificationStatus`} value={`${storageComponent.VerificationProgress ? storageComponent.VerificationProgress : 0}% ${translations && translations['in_progress'] ? translations['in_progress'] : 'In progress'}`}
                                className={`chip_${getVerificationStatus(storageComponent[field], translations, true)}`} />;
                        }
                        let labelText2 = getVerificationStatus(storageComponent[field], translations);
                        return <CustomChip key={`${component.ComponentID}_verificationStatus`} value={labelText2}
                            className={`chip_${getVerificationStatus(storageComponent[field], translations, true)}`} />;
                    case 'WipeMethod':
                        return <Typography key={`${component.ComponentID}_${index}_wipeMethod`}
                            style={{ fontSize: 12 }}
                            gutterBottom>{erasureMethods.data[storageComponent[field]]}</Typography>;
                    default:
                        return 'n/a';
                }
            });
            return (prettyPrint);
        }
    };

    const goToMobileProduct = (event, customerID, name) => {
        let mobileProductInventoryID = event.currentTarget.value;
        if (customerID !== activeCustomer) {
            setRedirectOptions({
                ...redirectOptions,
                open: true,
                product: name,
                customerID: customerID,
                redirectPath: `/products/mobile-products?InventoryID=${mobileProductInventoryID}`
            });
            return;
        }
        createHistory();
        setRedirectOptions({
            ...redirectOptions,
            redirect: true,
            mobileProductInventoryID: event.currentTarget.value,
            redirectPath: `/products/mobile-products?InventoryID=${mobileProductInventoryID}`
        });
    };

    const goToDisplayProduct = (event, customerID, name) => {
        let displayProductInventoryID = event.currentTarget.value;
        if (customerID !== activeCustomer) {
            setRedirectOptions({
                ...redirectOptions,
                open: true,
                product: name,
                customerID: customerID,
                redirectPath: `/products/displayProducts?InventoryID=${displayProductInventoryID}`
            });
            return;
        }
        createHistory();
        setRedirectOptions({
            ...redirectOptions,
            redirect: true,
            displayProductInventoryID: event.currentTarget.value,
            redirectPath: `/products/displayProducts?InventoryID=${displayProductInventoryID}`
        });
    };
    const goToStorageProduct = (event, customerID, name) => {
        let storageProductInventoryID = event.currentTarget.value;
        if (customerID !== activeCustomer) {
            setRedirectOptions({
                ...redirectOptions,
                open: true,
                product: name,
                customerID: customerID,
                redirectPath: `/products/storage-products?InventoryID=${storageProductInventoryID}`
            });
            return;
        }
        createHistory();
        setRedirectOptions({
            ...redirectOptions,
            redirect: true,
            storageProductInventoryID: event.currentTarget.value,
            redirectPath: `/products/storage-products?InventoryID=${storageProductInventoryID}`
        });
    };

    const goToPcProduct = (event, customerID, name) => {
        let pcProductInventoryID = event.currentTarget.value;
        if (customerID !== activeCustomer) {
            setRedirectOptions({
                ...redirectOptions,
                open: true,
                product: name,
                customerID: customerID,
                redirectPath: `/products/pc-products?InventoryID=${pcProductInventoryID}`
            });
            return;
        }
        createHistory();
        setRedirectOptions({
            ...redirectOptions,
            redirect: true,
            pcProductInventoryID: event.currentTarget.value,
            redirectPath: `/products/pc-products?InventoryID=${pcProductInventoryID}`
        });
    }

    const goToWatchProduct = (event, customerID, name) => {
        let watchProductInventoryID = event.currentTarget.value;
        if (customerID !== activeCustomer) {
            setRedirectOptions({
                ...redirectOptions,
                open: true,
                product: name,
                customerID: customerID,
                redirectPath: `/products/watch-products?InventoryID=${watchProductInventoryID}`
            });
            return;
        }
        createHistory();
        setRedirectOptions({
            ...redirectOptions,
            redirect: true,
            watchProductInventoryID: event.currentTarget.value,
            redirectPath: `/products/watch-products?InventoryID=${watchProductInventoryID}`
        });
    }

    const goToHeadphoneProduct = (event, customerID, name) => {
        let headphoneProductInventoryID = event.currentTarget.value;
        if (customerID !== activeCustomer) {
            setRedirectOptions({
                ...redirectOptions,
                open: true,
                product: name,
                customerID: customerID,
                redirectPath: `/products/headphone-products?InventoryID=${headphoneProductInventoryID}`
            });
            return;
        }
        createHistory();
        setRedirectOptions({
            ...redirectOptions,
            redirect: true,
            headphoneProductInventoryID: event.currentTarget.value,
            redirectPath: `/products/headphone-products?InventoryID=${headphoneProductInventoryID}`
        });
    }

    const handleChangeCustomer = async () => {
        await onChangeActiveCustomer(redirectOptions.customerID, redirectOptions?.redirectPath);
    };

    const createAction = (productType, productID, customerID, name, deviceOnlineStatus) => {
        const activeCustomerParams = `&ActiveCustomerID=${customerID}&ActiveCustomerName=${name}`;
        let link = `/mobile-product/details?productID=${productID}&type=MobileProduct${activeCustomerParams}`;
        switch (productType) {
            case 'PCProduct':
                link = `/pc-product/details?productID=${productID}&type=PCProduct${activeCustomerParams}`;
                break;
            case 'StorageProduct':
                link = `/storage-product/details?productID=${productID}&type=StorageProduct${activeCustomerParams}`;
                break;
            case 'WatchProduct':
                link = `/watch-product/details?productID=${productID}&type=WatchProduct${activeCustomerParams}`;
                break;
            case 'HeadphoneProduct':
                link = `/headphone-product/details?productID=${productID}&type=HeadphoneProduct${activeCustomerParams}`;
                break;
            default:
                break;
        }

        const goTo = (event) => {
            switch (productType) {
                case 'MobileProduct':
                    return hasPermission(permissions, 'product_list') ? goToMobileProductPage(event, customerID, name) : null;
                case 'PCProduct':
                    return hasPermission(permissions, 'product_list') ? goToPcProductPage(event, customerID, name) : null;
                case 'StorageProduct':
                    return hasPermission(permissions, 'product_list') ? goToStorageProductPage(event, customerID, name) : null;
                case 'WatchProduct':
                    return hasPermission(permissions, 'product_list') ? goToWatchProductPage(event, customerID, name) : null;
                case 'HeadphoneProduct':
                    return hasPermission(permissions, 'product_list') ? goToHeadphoneProductPage(event, customerID, name) : null;
                default:
                    return null
            }
        }

        return <>
            {productID ?
                <Tooltip title={translations["product_details"] || "Product details"}>
                    <a href={hasPermission(permissions, 'product_list') ? link : null} onClick={(e) => { e.preventDefault(); goTo({ currentTarget: { value: productID } }) }}>
                        <IconButton aria-label={translations["product_details"] || "Product details"}>
                            <FindInPage />
                        </IconButton>
                    </a>
                </Tooltip> : '[empty]'}
                <Tooltip component={'span'} title={translations['open_remote_erasure_anydesk'] || 'Open Securaze Remote AnyDesk session'} disabled={!deviceOnlineStatus || !deviceOnlineStatus.RemoteID || !deviceOnlineStatus.RemotePassword}>
                    <IconButton aria-label={translations['open_remote_erasure_anydesk'] || 'Open Securaze Remote AnyDesk session'}
                        onClick={(event) => handleOpenAnydesk(event, deviceOnlineStatus || {})}>
                        <SettingsRemote />
                    </IconButton>
                </Tooltip>
                <Tooltip component={'span'} title={translations['open_remote_erasure_rustdesk'] || 'Open Securaze Remote RustDesk session'} disabled={!deviceOnlineStatus || !deviceOnlineStatus.RemoteID || !deviceOnlineStatus.RemotePassword}>
                    <IconButton aria-label={translations['open_remote_erasure_rustdesk'] || 'Open Securaze Remote RustDesk session'}
                        onClick={(event) => handleOpenRustdesk(event, deviceOnlineStatus || {})}>
                        <SettingsRemote />
                    </IconButton>
                </Tooltip>
        </>
    };

    let wipeStatusLookup = { 'n/a': translations['short_not_assigned'] || 'n/a', 'erased': translations['erased'] || 'Erased', 'noterased': translations['not_erased'] || 'Not Erased', 'inprogress': translations['in_progress'] || 'In progress', 'error': translations['error'] || 'Error', 'warning': translations['warning'] || 'Warning', 'aborted': translations['report_aborted'] || 'Aborted by user', 'started': translations['started'] || 'Started' };
    let verificationStatusLookup = { 'n/a': translations['short_not_assigned'] || 'n/a', 'verified': translations['verified'] || 'Verified', 'notverified': translations['not_verified'] || 'Not Verified', 'inprogress': translations['in_progress'] || 'In progress', 'error': translations['error'] || 'Error', 'warning': translations['warning'] || 'Warning', 'aborted': translations['report_aborted'] || 'Aborted by user', 'started': translations['started'] || 'Started' };
    let productTypeLookup = { 'PCProduct': translations['pc_product'] || 'Work', 'MobileProduct': translations['mobile_product'] || 'Mobile', 'StorageProduct': translations['storage_product'] || 'Single disk-drives', 'workRemote': translations['work_remote'] || 'Work Remote', 'WatchProduct': translations['watch'] || 'Watch', 'HeadphoneProduct': translations['headphone'] || 'Headphone' };

    return (
        !isGroupsLoading &&
        <div className={classes.root}>
            {tableRef?.current?.state?.QueryType === 'ProductsMinimal' ? <ProductsMinimal
                productTypeLookup={productTypeLookup}
                updateView={updateView}
                createPaletteActionForProduct={createPaletteActionForProduct}
                createAction={createAction}
                handleChangeCustomer={handleChangeCustomer}
                activeCustomerName={activeCustomerName}
                tableRef={tableRef}
                currentView={currentView}
                method={erasureMethods.data}
                redirectOptions={redirectOptions}
                groupsLookup={groupsLookup} />

                : <ProductsAll
                    wipeStatusLookup={wipeStatusLookup}
                    verificationStatusLookup={verificationStatusLookup}
                    productTypeLookup={productTypeLookup}
                    updateView={updateView}
                    currentView={currentView}
                    createPaletteActionForProduct={createPaletteActionForProduct}
                    createAction={createAction}
                    handleChangeCustomer={handleChangeCustomer}
                    activeCustomerName={activeCustomerName}
                    redirectToCustomer={redirectToCustomer}
                    createPaletteAction={createPaletteAction}
                    getStorageComponentData={getStorageComponentData}
                    tableRef={tableRef}
                    method={erasureMethods.data}
                    redirectOptions={redirectOptions}
                    groups={groups}
                    groupsLookup={groupsLookup} />
            }
            {redirectOptions.open ?
                <ProductOverviewModal open={redirectOptions.open} activeCustomerName={activeCustomerName} product={redirectOptions.product} handleChangeCustomer={handleChangeCustomer} handleCloseSafe={() => setRedirectOptions({ ...redirectOptions, open: !redirectOptions.open })} translations={translations} activeCustomer={activeCustomer} />
                : null}
        </div >
    );
}

const mapDispatchToProps = dispatch => {
    return {
        onFetchErasureMethods: (user) => (dispatch(actions.fetchErasureMethod(user))),
        onChangeActiveCustomer: (customerID, path) => dispatch(action.changeActiveCustomer(customerID, path)),
        fetchGroups: params => dispatch(groupActions.fetchGroups(params)),
    };
};

const mapStateToProps = state => {
    return {
        activeCustomer: state?.core?.login?.activeCustomer?.CustomerID || null,
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
        permissions: state?.core?.login?.permissions || [],
        activeCustomerName: state?.core?.login?.activeCustomer?.Name,
        translations: state?.translate?.translations?.activeTranslations || {}
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(ProductOverview));
